import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdvancedFilterValue, AdvancedFiltersOptions } from '../../models/advancedFilters';
import { RootState } from '../store';

interface SegmentationPredictionFilterState {
  view?: AdvancedFilterValue;
  features: AdvancedFilterValue[];
  advancedFilters: AdvancedFiltersOptions;
  activeTags: AdvancedFilterValue[];
}

const initialState: SegmentationPredictionFilterState = {
  view: undefined,
  features: [],
  advancedFilters: {
    datasources: undefined,
    dicomICID: '',
    study: '',
    segmentationICID: '',
    keypointICID: '',
    technician: [],
    source_name: undefined,
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  },
  activeTags: [],
};

const segmentationPredictionFilterSlice = createSlice({
  name: 'segmentationPredictionFilter',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<AdvancedFilterValue | undefined>) => {
      state.view = action.payload;
    },
    setFeatures: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.features = action.payload;
    },
    setAdvancedFilter: (
      state,
      action: PayloadAction<{
        key: string;
        value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined;
      }>
    ) => {
      const { key, value } = action.payload;
      (state.advancedFilters as any)[key] = value;
    },
    resetFilters: state => {
      state.view = undefined;
      state.features = [];
      state.advancedFilters = {
        datasources: undefined,
        dicomICID: undefined,
        study: undefined,
        segmentationICID: undefined,
        keypointICID: undefined,
        technician: [],
        source_name: undefined,
        created_at_after: undefined,
        created_at_before: undefined,
        updated_at_after: undefined,
        updated_at_before: undefined,
      };
      state.activeTags = [];
    },
    updateActiveTags: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.activeTags = action.payload;
    },
    removeFilterTag: (state, action: PayloadAction<AdvancedFilterValue>) => {
      const removedTag = action.payload;
      const tagName = removedTag.name || '';
      const [rawKey] = tagName.split(':');
      const key = rawKey?.trim();

      if (key?.toLowerCase() === 'view') {
        state.view = undefined;
      } else if (key?.toLowerCase() === 'feature') {
        state.features = state.features.filter(f => f.uuid !== removedTag.uuid);
      } else if (key) {
        const currentVal = (state.advancedFilters as any)[key];
        if (Array.isArray(currentVal)) {
          const isStringArray = currentVal.length === 0 || typeof currentVal[0] === 'string';
          if (isStringArray) {
            (state.advancedFilters as any)[key] = currentVal.filter((item: any) => {
              const itemUuid = `${key}_${item}`;
              return itemUuid !== removedTag.uuid && item !== removedTag.uuid;
            });
          } else {
            (state.advancedFilters as any)[key] = currentVal.filter(
              (item: any) => item?.uuid !== removedTag.uuid
            );
          }
        } else if (currentVal && typeof currentVal === 'object') {
          if (currentVal.uuid === removedTag.uuid) {
            (state.advancedFilters as any)[key] = undefined;
          }
        } else if (typeof currentVal === 'string') {
          (state.advancedFilters as any)[key] = undefined;
        }
      }
    },
  },
});

export const {
  setView,
  setFeatures,
  setAdvancedFilter,
  resetFilters,
  updateActiveTags,
  removeFilterTag,
} = segmentationPredictionFilterSlice.actions;

export const selectSegmentationPredictionFilterState = (state: RootState) =>
  state.segmentationPredictionFilter;
export const selectSegmentationPredictionActiveFilters = (state: RootState) =>
  state.segmentationPredictionFilter.activeTags;

export default segmentationPredictionFilterSlice.reducer;
